/**
 * Returns a new array containing only distinct values.
 * @param values The array to process.
 * @returns A new array containing only distinct values.
 */
export function getDistinctValues<T>(values: T[]) {
  return Array.from(new Set(values));
}

/**
 * Checks whether two arrays contain the same sequence of elements.
 * @param a The first array.
 * @param b The second array.
 * @returns True if the arrays contain the same values in the same order.
 */
export function sequenceEqual<T>(a: T[], b: T[]) {
  if (a === b) {
    return true;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

export function groupBy<T, TKey = string | number>(arr: T[], selector: (element: T) => TKey) {
  const map = new Map<TKey, T[]>();
  arr.forEach((element) => {
    const key = selector(element);
    const group = map.get(key);
    if (!group) {
      map.set(key, [element]);
    } else {
      group.push(element);
    }
  });
  return map;
}

export function sortBy<T, TKey = string | number>(arr: T[], selector: (element: T) => TKey) {
  return arr.toSorted((a, b) => {
    const aVal = selector(a);
    const bVal = selector(b);
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal);
    }
    return (aVal as number) - (bVal as number);
  });
}

export function sortByDescending<T, TKey = string | number>(arr: T[], selector: (element: T) => TKey) {
  return arr.toSorted((a, b) => {
    const aVal = selector(a);
    const bVal = selector(b);
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return bVal.localeCompare(aVal);
    }
    return (bVal as number) - (aVal as number);
  });
}

/**
 * Creates a shuffled copy of the specified array using the Fisher-Yates algorithm.
 * @param arr The input array
 * @param prng An optional random number generator function. Uses Math.random if not specified.
 * @returns A new shuffled array.
 */
export function shuffle<T>(arr: T[], rngFunc?: () => number): T[] {
  rngFunc ??= Math.random;
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rngFunc() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
